import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMe } from '@/hooks/queries'
import { copyText } from '@/lib/clipboard'
import { notifyError, notifySuccess } from '@/lib/notify'
import { REFERRAL_PARAM, parseReferrer, referralUrl, saveReferrer } from '@/lib/referral'
import { useAuthStore } from '@/store/auth'
import { useOverlayStore } from '@/store/overlays'
import type { Giveaway, GiveawayObjective } from '@/models/giveaway'

/**
 * Copies the current user's referral link for a giveaway. Signed out, there is
 * no one to credit yet, so it asks them to sign in instead.
 */
export function useReferralLink(giveaway: Giveaway | undefined) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const openOverlay = useOverlayStore((state) => state.open)
  const { data: me } = useMe()
  const myId = me?.id ?? null

  return useCallback(
    async (objective: GiveawayObjective) => {
      if (!giveaway) return
      if (!isAuthenticated) {
        openOverlay('auth')
        return
      }
      if (myId === null) return

      const url = referralUrl(giveaway.get_slug, myId)
      if (!(await copyText(url))) {
        notifyError(`Could not copy your referral link. Copy it from here instead: ${url}`)
        return
      }
      const each = objective.entries_worth ?? 0
      notifySuccess(
        `Referral link copied. You get +${each} ${each === 1 ? 'entry' : 'entries'} for each person who enters through it.`,
      )
    },
    [giveaway, isAuthenticated, openOverlay, myId],
  )
}

/**
 * Remembers whose referral link (`?ref=`) brought the visitor to this
 * giveaway, so their first entry can credit them — even if signing in takes
 * them away from the page first.
 */
export function useCaptureReferral(giveawayId: number | null | undefined) {
  const [params] = useSearchParams()
  const referrer = parseReferrer(params.get(REFERRAL_PARAM))
  const { data: me } = useMe()
  const myId = me?.id ?? null

  useEffect(() => {
    if (giveawayId == null || referrer === null || referrer === myId) return
    saveReferrer(giveawayId, referrer)
  }, [giveawayId, referrer, myId])
}
